import { Card, Table, Tag, Space, Statistic, Row, Col, Button } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CarOutlined, CalendarOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'

interface CarRow {
  id: number
  vehicle?: { targa?: string; marca?: string; modello?: string }
}

interface AssignmentRow {
  id: number
  courtesy_car_id: number
  courtesy_car_targa?: string
  courtesy_car_modello?: string
  customer_nome?: string
  data_fine_prevista: string
  stato: string
}

const rentriColumns = [
  {
    title: 'Auto',
    key: 'auto',
    width: 100,
    render: (_: unknown, r: AssignmentRow) =>
      r.courtesy_car_targa
        ? <Tag color="blue" style={{ fontWeight: 600 }}>{r.courtesy_car_targa}</Tag>
        : `#${r.courtesy_car_id}`,
  },
  {
    title: 'Cliente',
    dataIndex: 'customer_nome',
    key: 'cliente',
    render: (v: string) => v ?? '—',
    ellipsis: true,
  },
  {
    title: 'Rientro previsto',
    dataIndex: 'data_fine_prevista',
    key: 'rientro',
    width: 130,
    render: (v: string) => {
      const d = dayjs(v)
      const isToday = d.isSame(dayjs(), 'day')
      return (
        <Tag color={isToday ? 'orange' : 'cyan'}>
          {d.format('DD/MM HH:mm')}
        </Tag>
      )
    },
  },
]

export function TableAutoCortesiaWidget() {
  const navigate = useNavigate()
  const today = dayjs().format('YYYY-MM-DD')
  const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD')

  const { data: availableCars } = useQuery<CarRow[]>({
    queryKey: ['dashboard', 'auto-cortesia-disponibili-list'],
    queryFn: async () => {
      const res = await axiosInstance.get('/courtesy-cars/available')
      return Array.isArray(res.data) ? res.data : []
    },
    staleTime: 60000,
  })

  const { data: rientri, isLoading: loadingRientri } = useQuery<AssignmentRow[]>({
    queryKey: ['dashboard', 'auto-cortesia-rientri', today, tomorrow],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/courtesy-cars/assignments?date_from=${today}&date_to=${tomorrow}&stato=in_corso`
      )
      return Array.isArray(res.data) ? res.data : []
    },
    staleTime: 60000,
  })

  const inUso = (rientri ?? []).length
  const rentriOggi = (rientri ?? []).filter(r =>
    dayjs(r.data_fine_prevista).isSame(dayjs(), 'day')
  ).length

  return (
    <Card
      size="small"
      title="Auto di cortesia"
      extra={
        <Button
          size="small"
          icon={<CalendarOutlined />}
          onClick={() => navigate('/courtesy-cars/planning')}
        >
          Pianificazione
        </Button>
      }
    >
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Statistic
            title="In uso ora"
            value={inUso}
            prefix={<CarOutlined />}
            valueStyle={{ color: inUso > 0 ? '#1677ff' : '#52c41a', fontSize: 20 }}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title="Rientri oggi"
            value={rentriOggi}
            prefix={<CalendarOutlined />}
            valueStyle={{ color: rentriOggi > 0 ? '#fa8c16' : '#8c8c8c', fontSize: 20 }}
          />
        </Col>
      </Row>

      {(rientri ?? []).length > 0 && (
        <Table
          columns={rentriColumns}
          dataSource={rientri ?? []}
          rowKey="id"
          size="small"
          loading={loadingRientri}
          pagination={false}
          scroll={{ x: 350 }}
          locale={{ emptyText: 'Nessun rientro previsto' }}
          title={() => (
            <span style={{ fontSize: 12, fontWeight: 600 }}>
              Rientri previsti oggi/domani
            </span>
          )}
          onRow={() => ({
            onClick: () => navigate('/courtesy-cars/planning'),
            style: { cursor: 'pointer' },
          })}
        />
      )}

      {(rientri ?? []).length === 0 && (availableCars ?? []).length > 0 && (
        <Space style={{ color: '#52c41a', fontSize: 13 }}>
          <CarOutlined />
          {availableCars?.length} auto disponibili
        </Space>
      )}
    </Card>
  )
}
